import React, { useEffect, useMemo } from "react";
import { Box, Typography, Grid, useTheme, useMediaQuery } from "@mui/material";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const scrollRightToLeft = keyframes`
  0% { transform: translateX(0%); }
  100% { transform: translateX(-50%); }
`;

const ScrollingTrack = styled(Box)(({ gap }) => ({
  display: "flex",
  alignItems: "center",
  gap: gap || "12px",
  width: "max-content",
  animation: `${scrollRightToLeft} 45s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

const CMS_SECTION_PAGE = "Monthly donation";
const CMS_SECTION_NAME = "How it helps us";

// Build images from monthly donation API doners_image: [{ id, url }]
const getImagesFromDonersImage = (donersImage) => {
  if (!donersImage || !Array.isArray(donersImage)) return [];
  return donersImage
    .filter((item) => item?.url)
    .map((item) => ({ src: item.url, alt: `Partner` }));
};

// Fallback: build images from CMS relatedCMS (only used when doners_image is empty)
const getImagesFromRelatedCms = (relatedCMS) => {
  if (!relatedCMS || !Array.isArray(relatedCMS)) return [];
  const related = relatedCMS.filter((r) => r?.status === 1 || r?.status === true);
  const images = [];
  if (related[0]) {
    if (related[0].image) images.push(related[0].image);
    if (related[0].image2) images.push(related[0].image2);
    if (related[0].image3) images.push(related[0].image3);
  }
  if (related[1]) {
    if (related[1].image) images.push(related[1].image);
    if (related[1].image2) images.push(related[1].image2);
  }
  return images;
};

const HelpUs = ({ donersImage, donationData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const isMediumScreen = useMediaQuery('(min-width: 900px) and (max-width: 1200px)');
  const isSmallScreen = useMediaQuery('(max-width: 899px)');
  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const helpSectionCms = useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    return data.find(
      (s) => s?.page === CMS_SECTION_PAGE && s?.section === CMS_SECTION_NAME
    ) || null;
  }, [cmsHome]);

  const apiImages = useMemo(
    () => getImagesFromDonersImage(donersImage),
    [donersImage]
  );

  const cmsImages = useMemo(
    () => getImagesFromRelatedCms(helpSectionCms?.relatedCMS),
    [helpSectionCms?.relatedCMS]
  );

  const imagesToShow = useMemo(() => {
    if (apiImages.length > 0) {
      return apiImages.map((item, idx) => ({ ...item, alt: item.alt + ` ${idx + 1}` }));
    }
    if (cmsImages.length > 0) {
      return cmsImages.map((src, idx) => ({ src, alt: `Partner ${idx + 1}` }));
    }
    return [];
  }, [apiImages, cmsImages]);

  const hasData = imagesToShow.length > 0;
  if (!hasData) return null;

  const sectionLabel = helpSectionCms?.section_title ?? "How it helps us";
  const title = "How Will Your Donation Help Us?";
  const description =
    donationData?.monthly_header_description2 ||
    helpSectionCms?.description ||
    "";

  const useScrollLayout = imagesToShow.length > 5;

  const cardSx = {
    background: "#fff",
    border: "1px solid #E8E8E8",
    borderRadius: isMobile ? "7px" : "24px",
    p: isSmallScreen ? "8px" : "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
    flexShrink: 0,
    width: isMobile ? "90px" : "230px",
    minWidth: isMobile ? "90px" : "230px",
    maxWidth: isMobile ? "90px" : "230px",
    height: isMobile ? undefined : "130px",
    minHeight: isMobile ? "46px" : "130px",
  };

  return (
    <Box
      sx={{
        width: "94.4%",
        mx: "auto",
        py: { xs: 4, md: 8 },
        background: "#fff",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          sx={{
            fontSize: isMobile ? "10px" : "13px",
            color: "#131313",
            fontWeight: 400,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            mb: isMobile ? "10px" : "23px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#A9C74E",
              fontSize: "20px",
              lineHeight: 1,
            }}
          >
            ●
          </Box>
          {sectionLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: isMobile ? "18px" : isSmallScreen ? "24px" : isMediumScreen ? "30px" : "36px",
            fontWeight: 800,
            color: "#1D1D1B",
            mb: isMobile ? "10px" : 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "16px",
            color: "#7E7878",
            maxWidth: "975px",
            mx: "auto",
            fontWeight: 400,
            mb: isMobile ? "10px" : 4,
          }}
        >
          {description}
        </Typography>
      </Box>

      {useScrollLayout ? (
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            position: "relative",
            py: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "40px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              width: "40px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
          }}
        >
          <ScrollingTrack gap={isMobile ? "8px" : "12px"}>
            {imagesToShow.map((item, idx) => (
              <Box key={`first-${idx}`} sx={cardSx}>
                <Box
                  component="img"
                  src={item.src}
                  alt={item.alt}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: isMobile ? "none" : "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
            {imagesToShow.map((item, idx) => (
              <Box key={`second-${idx}`} sx={cardSx}>
                <Box
                  component="img"
                  src={item.src}
                  alt={item.alt}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: isMobile ? "none" : "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
          </ScrollingTrack>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "8px" : "12px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {imagesToShow.map((item, idx) => (
            <Grid
              item
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: isMobile ? "auto" : "0 0 auto",
                maxWidth: isMobile ? "90px" : "230px",
                minWidth: isMobile ? undefined : "230px",
              }}
            >
              <Box sx={cardSx}>
                <Box
                  component="img"
                  src={item.src}
                  alt={item.alt}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: isMobile ? "none" : "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HelpUs;
